import { Component, computed, input, output } from '@angular/core';
import * as fromSeriousSystem from '@serious-system';

/**
 * One row in the share access UI — the Serious System "UserAccessDropdownListItem"
 * node (Owner / User / Group variants, all 416×56). A light avatar with initials,
 * a semibold title, and a caption secondary line ("Owner · email", an email, or
 * "Group · N members"). The owner/user/group distinction is purely content: the
 * caller composes `prefix` + `detail`; the avatar/title/layout are identical.
 * `removable` adds a trailing remove control (used in the selected list — not
 * part of the base node, so it's a deliberate addition).
 */
@Component({
  selector: 'squadbox-assistant-share-access-row',
  imports: [
    fromSeriousSystem.AvatarDirective,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
  ],
  template: `
    <div class="flex items-center gap-4 h-14 p-2 rounded-lg w-full">
      <div class="flex flex-1 items-center gap-3 min-w-0">
        <div
          sdAvatar
          size="sm"
          color="light"
          class="pointer-events-none font-semibold shrink-0"
        >
          {{ initials() }}
        </div>
        <div class="flex flex-col justify-center min-w-0">
          <span class="typo-p3 font-semibold leading-5 truncate w-full">{{
            title()
          }}</span>
          <span
            class="typo-caption text-neutral-500 leading-4 truncate w-full"
            >{{ secondary() }}</span
          >
        </div>
      </div>
      @if (removable()) {
        <button
          sdIconButton
          variant="icon"
          color="neutral"
          size="xs"
          (click)="remove.emit(); $event.stopPropagation()"
        >
          <svg sdUseUIBasicIcon="close"></svg>
        </button>
      }
    </div>
  `,
})
export class AssistantShareAccessRowComponent {
  public readonly initials = input<string>('-');
  public readonly title = input.required<string>();
  /** Optional leading qualifier ("Owner" / "Group"); joined with " · ". */
  public readonly prefix = input<string>('');
  /** Trailing detail — an email, or "N members". */
  public readonly detail = input<string>('');
  public readonly removable = input<boolean>(false);

  public readonly remove = output<void>();

  protected readonly secondary = computed(() => {
    const prefix = this.prefix();
    const detail = this.detail();
    return prefix && detail ? `${prefix} · ${detail}` : prefix || detail;
  });
}
